import React, { useState, useRef, useEffect, useCallback } from 'react';

const BeforeAfterSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Auto-animate slider with smooth loop
  useEffect(() => {
    if (!isDragging) {
      let position = sliderPosition;
      
      const animate = () => {
        // Smooth continuous loop from 20 to 80 and back to 20
        position += 0.2;
        
        // Loop back when reaching the end
        if (position >= 80) {
          position = 20;
        }
        
        setSliderPosition(position);
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isDragging, sliderPosition]);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = (x / rect.width) * 100;
    
    setSliderPosition(Math.min(Math.max(percent, 0), 100));
  }, []);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) handleMove(e.clientX);
    };

    const handleTouchMove = (e) => {
      if (isDragging) handleMove(e.touches[0].clientX);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMove, handleMouseUp]);

  const handleMouseDown = () => setIsDragging(true);

  return (
    <div className="space-y-4">
      <h3 className="font-['Bebas_Neue'] text-2xl text-white text-center tracking-wide">
        {title}
      </h3>
      
      <div 
        ref={containerRef}
        className="relative w-full aspect-[4/3] overflow-hidden rounded-lg cursor-ew-resize select-none shadow-2xl"
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        {/* Container for both images - same size */}
        <div className="absolute inset-0">
          {/* After Image (Bottom Layer) */}
          <img 
            src={afterImage}
            alt="After cleaning"
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
          
          {/* Before Image (Top Layer) - Clipped with clip-path */}
          <img 
            src={beforeImage}
            alt="Before cleaning"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
            }}
            draggable="false"
          />
        </div>
        
        {/* Slider Line and Handle */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
            <div className="flex gap-1">
              <div className="w-0.5 h-6 bg-gray-800"></div>
              <div className="w-0.5 h-6 bg-gray-800"></div>
            </div>
          </div>
        </div>
        
        {/* Labels */}
        <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-semibold z-20">
          Before
        </div>
        <div className="absolute top-4 right-4 bg-[#CC0000] text-white px-3 py-1.5 rounded-full text-sm font-semibold z-20">
          After
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
